import { Check, Layers3, Sparkles, Target } from 'lucide-react'
import type { DesignSystemState } from '../lib/design-system-options'
import { useDesignSystem } from '../hooks/use-design-system'
import { useStudioLocale } from '@/i18n'
import { cn } from '@/lib/utils'
import { getDesignDirectionCopy } from './design-direction-copy'

type DirectionId = 'calm' | 'bold' | 'compact'

const DIRECTIONS: Array<{
  id: DirectionId
  icon: typeof Sparkles
  axes: DesignSystemState
  swatches: readonly [string, string, string]
}> = [
  {
    id: 'calm',
    icon: Sparkles,
    axes: {
      style: 'luma',
      baseColor: 'neutral',
      theme: 'blue',
      chart: 'blue',
      radius: 'medium',
      font: 'noto-sans',
    },
    swatches: ['bg-blue-600', 'bg-slate-200', 'bg-white'],
  },
  {
    id: 'bold',
    icon: Layers3,
    axes: {
      style: 'luma',
      baseColor: 'zinc',
      theme: 'violet',
      chart: 'violet',
      radius: 'large',
      font: 'space-grotesk',
    },
    swatches: ['bg-violet-600', 'bg-fuchsia-200', 'bg-zinc-950'],
  },
  {
    id: 'compact',
    icon: Target,
    axes: {
      style: 'luma',
      baseColor: 'stone',
      theme: 'green',
      chart: 'green',
      radius: 'small',
      font: 'inter',
    },
    swatches: ['bg-success', 'bg-stone-200', 'bg-stone-950'],
  },
]

function directionMatches(
  state: DesignSystemState,
  candidate: DesignSystemState,
): boolean {
  return Object.entries(candidate).every(
    ([axis, value]) => state[axis as keyof DesignSystemState] === value,
  )
}

export function DesignDirectionChooser() {
  const { locale } = useStudioLocale()
  const copy = getDesignDirectionCopy(locale)
  const { designSystem, setBulk } = useDesignSystem()

  return (
    <section
      className="rounded-2xl border border-border bg-card p-4 shadow-sm sm:p-5"
      data-testid="design-direction-chooser"
      aria-labelledby="design-direction-title"
    >
      <div className="max-w-3xl">
        <p className="text-[11px] font-bold uppercase tracking-[0.12em] text-primary">
          {copy.eyebrow}
        </p>
        <h2 id="design-direction-title" className="mt-1 text-lg font-semibold text-foreground">
          {copy.title}
        </h2>
        <p className="mt-1 text-sm leading-6 text-muted-foreground">{copy.body}</p>
      </div>

      <div className="mt-4 grid gap-3 lg:grid-cols-3">
        {DIRECTIONS.map((direction) => {
          const active = directionMatches(designSystem, direction.axes)
          const directionCopy = copy.directions[direction.id]
          const Icon = direction.icon
          return (
            <button
              key={direction.id}
              type="button"
              aria-pressed={active}
              onClick={() => setBulk(direction.axes)}
              className={cn(
                'group relative min-h-36 rounded-xl border p-4 text-left transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-ring/30',
                active
                  ? 'border-primary/50 bg-primary/5'
                  : 'border-border bg-background hover:border-foreground/25 hover:bg-muted/30',
              )}
              data-testid={`design-direction-${direction.id}`}
            >
              <span className="flex items-start justify-between gap-3">
                <span className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-border bg-card text-foreground">
                  <Icon className="h-4 w-4" aria-hidden />
                </span>
                <span className="flex -space-x-1" aria-hidden>
                  {direction.swatches.map((swatch) => (
                    <span key={swatch} className={cn('h-6 w-6 rounded-full border-2 border-card', swatch)} />
                  ))}
                </span>
              </span>
              <span className="mt-3 block text-sm font-semibold text-foreground">
                {directionCopy.title}
              </span>
              <span className="mt-1 block text-xs leading-5 text-muted-foreground">
                {directionCopy.detail}
              </span>
              {active ? (
                <span className="mt-3 inline-flex items-center gap-1 text-[11px] font-semibold text-primary">
                  <Check className="h-3.5 w-3.5" aria-hidden />
                  {copy.selected}
                </span>
              ) : null}
            </button>
          )
        })}
      </div>
    </section>
  )
}
